// src/pages/Fees.jsx
import React from "react";
import { motion } from "framer-motion";

export default function Fees() {
  // Reuse this data for both the desktop table and the mobile cards
  const feeRows = [
    // Trading
    { service: "Quick / Exchange Buy", fee: "0.5 %", taxes: "18 % GST on fee" },
    {
      service: "Quick / Exchange Sell",
      fee: "0.5 %",
      taxes: "18 % GST on fee + 1 % TDS on trade amount",
    },
    // INR withdrawals
    {
      service: "INR Withdrawal – Standard",
      fee: "₹25",
      taxes: "18 % GST on fee (₹4.50)",
    },
    {
      service: "INR Withdrawal – Instant",
      fee: "₹125",
      taxes: "18 % GST on fee (₹22.50)",
    },
    // Crypto withdrawals
    { service: "USDT TRC20 Withdrawal", fee: "5 USDT", taxes: "Network fee (no GST)" },
    { service: "USDT ERC20 Withdrawal", fee: "8 USDT", taxes: "Network fee (no GST)" },
    { service: "USDT BEP20 Withdrawal", fee: "5 USDT", taxes: "Network fee (no GST)" },
    // Inactivity
    {
      service: "Monthly Inactivity Fee",
      fee: "₹500",
      taxes: "18 % GST on fee (₹90)",
    },
  ];

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-900 pb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <header className="bg-white dark:bg-slate-800 shadow-md border-b-4 border-blue-600">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-800 dark:text-slate-100 mb-3 sm:mb-4">
            Fee Structure
          </h1>
          <p className="text-blue-600 dark:text-slate-300 text-base sm:text-lg md:text-xl max-w-2xl mx-auto">
            Clear, transparent pricing for every trade, deposit, withdrawal, and account action.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 sm:mt-12 space-y-12 sm:space-y-16">
        {/* Trading Fees */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-blue-100 dark:border-slate-700 p-4 sm:p-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-slate-100 mb-4 sm:mb-6">
            Trading Fees
          </h2>
          <p className="text-blue-800/90 dark:text-slate-200 leading-relaxed mb-4 text-sm sm:text-base">
            Coinzetto currently offers two trading modes—Quick Trade and Exchange (order-book) Trade.
            The platform fee is identical for both modes.
          </p>
          <ul className="list-disc list-inside text-blue-800/90 dark:text-slate-200 space-y-3 text-sm sm:text-base">
            <li>
              <strong>Quick Trade / Exchange Trade (Buy Orders):</strong>&nbsp;
              0.5 % platform fee <em>+ 18 % GST on the fee amount</em>.
            </li>
            <li>
              <strong>Quick Trade / Exchange Trade (Sell Orders):</strong>&nbsp;
              0.5 % platform fee <em>+ 18 % GST on the fee amount</em>
              &nbsp;<u>+ 1 % TDS withheld on the trade amount</u> (per Indian income-tax regulations).
            </li>
          </ul>
        </section>

        {/* INR Withdrawals */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-blue-100 dark:border-slate-700 p-4 sm:p-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-slate-100 mb-4 sm:mb-6">
            INR Withdrawal Fees
          </h2>
          <div className="text-blue-800/90 dark:text-slate-200 space-y-4 text-sm sm:text-base">
            <div>
              <strong>Standard Withdrawal:</strong>&nbsp;
              ₹25 fee <em>+ 18 % GST on the fee</em> (₹29.50 total).<br />
              <span className="block mt-1">
                • Processing time: <u>up to 3 business days</u>.
              </span>
              <span className="block">
                • Amount limits: <u>₹1,000 – ₹500,000</u> per request.
              </span>
            </div>
            <div>
              <strong>Instant Withdrawal:</strong>&nbsp;
              ₹125 fee <em>+ 18 % GST on the fee</em> (₹147.50 total).<br />
              <span className="block mt-1">
                • Processing time: <u>2–3 hours</u> (may extend to 3 business days if banks experience delays).
              </span>
              <span className="block">
                • Amount limits: <u>₹1,000 – ₹50,000</u> per request.
              </span>
            </div>
          </div>
        </section>

        {/* Crypto Withdrawals */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-blue-100 dark:border-slate-700 p-4 sm:p-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-slate-100 mb-4 sm:mb-6">
            Crypto Withdrawal Fees
          </h2>
          <p className="text-blue-800/90 dark:text-slate-200 leading-relaxed mb-2 text-sm sm:text-base">
            Each crypto withdrawal incurs a fixed on-chain network fee. These fees are
            deducted in the native currency at the time of withdrawal:
          </p>
          <ul className="list-disc list-inside pl-4 text-blue-800/90 dark:text-slate-200 space-y-2 text-sm sm:text-base">
            <li>USDT TRC20 — 5 USDT</li>
            <li>USDT ERC20 — 8 USDT</li>
            <li>USDT BEP20 — 5 USDT</li>
            <li>BTC, ETH, and others — dynamic, shown in-app before confirmation</li>
          </ul>
        </section>

        {/* Taxes */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-blue-100 dark:border-slate-700 p-4 sm:p-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-slate-100 mb-4 sm:mb-6">
            Taxes
          </h2>
          <p className="text-blue-800/90 dark:text-slate-200 leading-relaxed text-sm sm:text-base">
            All platform service charges attract 18 % Goods and Services Tax (GST), which is added on top of
            the fee amount and itemised in your invoices. For sell orders, Coinzetto withholds 1 % TDS on
            the gross trade value and remits it to the Income-tax Department, as mandated under Section 194S.
          </p>
        </section>

        {/* Inactivity Charges */}
        <section className="bg-white dark:bg-slate-800 rounded-2xl shadow-md border border-blue-100 dark:border-slate-700 p-4 sm:p-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-slate-100 mb-4 sm:mb-6">
            Inactivity Charges
          </h2>
          <p className="text-blue-800/90 dark:text-slate-200 leading-relaxed mb-2 text-sm sm:text-base">
            If an account shows <u>no trading, deposit, or withdrawal activity for 30 consecutive days</u>,
            it becomes inactive.
          </p>
          <p className="text-blue-800/90 dark:text-slate-200 leading-relaxed text-sm sm:text-base">
            A maintenance fee of ₹500 + 18 % GST (₹590 total) will be charged every month until the user performs
            at least one transaction.
          </p>
        </section>

        {/* Summary Table */}
        <section className="space-y-4">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-slate-100">
            Summary Table
          </h2>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {feeRows.map((row, idx) => (
              <div
                key={idx}
                className="bg-white dark:bg-slate-800 rounded-xl border border-blue-100 dark:border-slate-700 shadow-sm p-3"
              >
                <p className="text-sm font-semibold text-blue-800 dark:text-slate-100">
                  {row.service}
                </p>
                <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-blue-800/90 dark:text-slate-200">
                  <div className="opacity-80">Fee</div>
                  <div className="text-right font-medium">{row.fee}</div>
                  <div className="opacity-80">Taxes</div>
                  <div className="text-right">{row.taxes}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto shadow rounded-2xl bg-white dark:bg-slate-800 border border-blue-100 dark:border-slate-700">
            <table className="min-w-full text-blue-800/90 dark:text-slate-200 text-sm">
              <thead className="bg-blue-50 dark:bg-slate-900/60 text-blue-800 dark:text-slate-100">
                <tr>
                  <th className="px-6 py-3 text-left">Service</th>
                  <th className="px-6 py-3 text-left">Fee</th>
                  <th className="px-6 py-3 text-left">Taxes</th>
                </tr>
              </thead>
              <tbody>
                {feeRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className="border-t border-blue-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-900/60"
                  >
                    <td className="px-6 py-4">{row.service}</td>
                    <td className="px-6 py-4">{row.fee}</td>
                    <td className="px-6 py-4">{row.taxes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-2">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-slate-100 mb-3 sm:mb-4">
            Need More Details?
          </h2>
          <p className="text-blue-800/90 dark:text-slate-200 mb-5 text-sm sm:text-base">
            Check out our FAQs or reach out to the 24 / 7 support desk for any fee-related queries.
          </p>
          <a
            href="/support-ticket"
            className="inline-flex items-center justify-center bg-blue-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg hover:bg-blue-700 active:scale-[0.99] transition"
          >
            Contact Support
          </a>
        </section>
      </main>
    </motion.div>
  );
}
